import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";

import {
  fetchAudit,
  type AuditCoverageDay,
  type AuditEntry,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/ui/data-table";
import { DataTableShell } from "@/components/ui/data-table-shell";
import { Card, CardContent } from "@/components/ui/card";
import { useCatalog, type Translate } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";
import { viewerInstant } from "@/app/displayFormat";

const PAGE_SIZE = 100;
// How long typing pauses before it is a query: every query reads up to a full
// page's budget and is itself recorded as a view of the log.
const FILTER_DEBOUNCE_MS = 1500;

/**
 * Both ends of the window, from one instant.
 *
 * Sending only `from` and letting the server default the end to "now" looks
 * simpler and is wrong twice. The span then grows by a day for every day the
 * tab stays open, so a long period eventually trips the server's cap and the
 * page fails with a range error nobody asked for. An explicit,
 * pinned pair fixes that; the Refresh button is how you ask for a newer one.
 */
function windowFor(days: number, anchor: number): { from: string; to: string } {
  return {
    from: new Date(anchor - days * 86_400_000).toISOString(),
    to: new Date(anchor).toISOString(),
  };
}

// The widest window the API answers (HISTORY_DAYS in web/audit_routes.py).
const MAX_WINDOW_DAYS = 3650;

/** A calendar day in the browser's time zone, as the date inputs give it. */
function dayString(t: number): string {
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The three ways to choose the period read. */
type Period = "recent" | "dates" | "all";

type Range =
  | { kind: "recent"; days: number }
  // Whole days, from the start of `from` to the end of `to` (or now, for today).
  | { kind: "dates"; from: string; to: string }
  | { kind: "all" };

/** Why a chosen period gives no window; translated at render. */
type WindowProblem = "days_range" | "both_dates" | "order" | "too_long";

type Window = { from: string; to: string } | { all: true; to: string } | { problem: WindowProblem };

function ymd(day: string): [number, number, number] {
  const [y, m, d] = day.split("-").map(Number);
  return [y, m - 1, d];
}

/** The window for a range, pinned at `anchor` like windowFor; or why there is none. */
function windowOf(range: Range, anchor: number): Window {
  // Pinned at the anchor too, so paging back to the first page shows the same one.
  if (range.kind === "all") return { all: true, to: new Date(anchor).toISOString() };
  if (range.kind === "recent") {
    if (!Number.isInteger(range.days) || range.days < 1 || range.days > MAX_WINDOW_DAYS) {
      return { problem: "days_range" };
    }
    return windowFor(range.days, anchor);
  }
  if (!range.from || !range.to) return { problem: "both_dates" };
  const start = new Date(`${range.from}T00:00:00`).getTime();
  const dayAfter = new Date(`${range.to}T00:00:00`);
  dayAfter.setDate(dayAfter.getDate() + 1);
  const end = Math.min(dayAfter.getTime(), anchor);
  if (!(start < end)) return { problem: "order" };
  // Counted in calendar days: a range crossing a daylight-saving change is an hour off.
  if (Date.UTC(...ymd(range.to)) - Date.UTC(...ymd(range.from)) >= MAX_WINDOW_DAYS * 86_400_000) {
    return { problem: "too_long" };
  }
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
}

function statusTone(status: number | null): string {
  if (status === null) return "opacity-60";
  if (status >= 500) return "text-destructive";
  if (status >= 400) return "text-warning-content";
  return "text-success";
}

/**
 * An actor is a user, a machine label ("stripe"), or nobody. The third case is
 * not an error: the actor is resolved from a session, and a login attempt has
 * none yet — so every login, successful or not, is anonymous here.
 */
function actorBadge(t: Translate, e: AuditEntry) {
  if (e.actor_kind === "user") {
    return (
      <span className="whitespace-nowrap">
        {e.actor}
        {e.actor_role === "admin" && (
          <Badge variant="outline" className="ml-1.5 text-[10px]">{t("operator.ops.audit.actor_admin")}</Badge>
        )}
      </span>
    );
  }
  if (e.actor_kind === "label") {
    return <span className="font-mono text-xs opacity-80">{e.actor}</span>;
  }
  return <span className="opacity-50">{t("operator.ops.audit.actor_anonymous")}</span>;
}

function auditColumns(t: Translate): ColumnDef<AuditEntry, any>[] {
  return [
  {
    accessorKey: "ts",
    header: t("operator.ops.audit.when"),
    cell: ({ row }) => (
      <span className="font-mono text-xs whitespace-nowrap">
        {viewerInstant(row.original.ts, "dateTimeSeconds")}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: t("operator.ops.audit.status"),
    cell: ({ row }) => (
      <span className={`font-mono text-xs ${statusTone(row.original.status)}`}>
        {row.original.status ?? "—"}
      </span>
    ),
  },
  {
    accessorKey: "method",
    header: t("operator.ops.audit.method"),
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.method ?? "—"}</span>,
  },
  { accessorKey: "actor", header: t("operator.ops.audit.actor"), cell: ({ row }) => actorBadge(t, row.original) },
  {
    accessorKey: "endpoint",
    header: t("operator.ops.audit.endpoint"),
    cell: ({ row }) =>
      // The writer's fallback row: the call happened but its entry could not be
      // written in full, so every other column is empty for a reason.
      row.original.error ? (
        <span className="text-xs text-destructive">
          {t("operator.ops.audit.not_recorded", { error: String(row.original.error) })}
        </span>
      ) : (
        <span className="font-mono text-xs">
          {row.original.endpoint ?? <span className="opacity-50">{t("operator.ops.audit.no_route")}</span>}
        </span>
      ),
  },
  {
    accessorKey: "path",
    header: t("operator.ops.audit.path"),
    cell: ({ row }) => (
      <span className="font-mono text-xs break-all">
        {row.original.path ?? "—"}
        {/* Present only once request input is opted in. */}
        {row.original.query ? <span className="opacity-60">?{row.original.query}</span> : null}
      </span>
    ),
  },
  {
    id: "body",
    header: t("operator.ops.audit.body"),
    cell: ({ row }) => {
      const e = row.original;
      if (e.body_truncated) return <span className="text-xs opacity-60">{t("operator.ops.audit.body_too_large")}</span>;
      if (e.body === undefined) return <span className="text-xs opacity-40">{t("operator.ops.audit.body_hidden")}</span>;
      if (e.body === null) return <span className="text-xs opacity-40">—</span>;
      return (
        <details className="text-xs">
          <summary className="cursor-pointer opacity-70">{t("operator.ops.audit.body_show")}</summary>
          <pre className="mt-1 max-w-[420px] overflow-x-auto whitespace-pre-wrap break-all rounded bg-muted p-2">
            {JSON.stringify(e.body, null, 2)}
          </pre>
        </details>
      );
    },
  },
  ];
}

/**
 * Which days have anything recorded.
 *
 * The writer creates a day's file on its first entry, so a day with no file
 * has no recorded data. That is NOT a quiet day: no changes and a writer that
 * was stopped or failing leave the same gap, and the log cannot tell which.
 * It is drawn as its own state, never as a zero.
 *
 * Two further states earn their own rendering:
 *
 *   partial        the window clips this day — a 7-day range chosen at 14:00
 *                  covers only the last ten hours of its oldest day. The bar
 *                  still counts the whole day; the table shows only the part
 *                  inside the window.
 *   recorded null  the directory could not be listed, so nothing is known
 *                  about any day. Drawn as unknown, like the no-data state
 *                  but louder.
 */
function CoverageBar({ t, days }: { t: Translate; days: AuditCoverageDay[] }) {
  if (!days.length) return null;
  const busiest = Math.max(...days.map((d) => d.entries), 1);

  const state = (d: AuditCoverageDay) =>
    d.recorded === null ? "unknown" : d.recorded ? "recorded" : "no-data";

  const describe = (d: AuditCoverageDay) =>
    t("operator.ops.audit.day_tooltip", {
      day: d.day,
      state: state(d) === "no-data" ? "no_data" : state(d),
      partial: d.partial ? "yes" : "no",
      count: d.entries,
    });

  const tone = (d: AuditCoverageDay) => {
    if (d.recorded === null) return "bg-destructive/40";
    if (!d.recorded) return "bg-muted-foreground/20";
    return d.partial ? "bg-primary/40" : "bg-primary/70";
  };

  return (
    <div className="flex flex-wrap items-end gap-[2px]" aria-label={t("operator.ops.audit.entries_per_day")}>
      {days.map((d) => (
        <div
          key={d.day}
          title={describe(d)}
          data-day={d.day}
          data-state={state(d)}
          data-partial={d.partial || undefined}
          className={`w-2 rounded-sm ${tone(d)} ${d.partial ? "outline-dashed outline-1 outline-offset-0 outline-primary/40" : ""}`}
          style={{ height: `${d.recorded ? 6 + (d.entries / busiest) * 26 : 4}px` }}
        />
      ))}
    </div>
  );
}

const EMPTY_FILTERS = { actor: "", endpoint: "", path: "", status: "" };
type FilterState = typeof EMPTY_FILTERS;

export function AuditLogPage() {
  const { t, catalog } = useCatalog();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = React.useMemo(() => auditColumns(t), [catalog]);
  // What each choice was last set to, so switching between them keeps it.
  const [period, setPeriod] = useState<Period>("recent");
  const [daysText, setDaysText] = useState("7");
  const [days, setDays] = useState(7);
  const [datesText, setDatesText] = useState(() => ({
    from: dayString(Date.now() - 30 * 86_400_000), to: dayString(Date.now()),
  }));
  const [dates, setDates] = useState(datesText);
  const range: Range = period === "recent" ? { kind: "recent", days }
    : period === "dates" ? { kind: "dates", ...dates } : { kind: "all" };
  const [anchor, setAnchor] = useState<number>(() => Date.now());
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS);
  const [includeBodies, setIncludeBodies] = useState(false);
  const [includeViews, setIncludeViews] = useState(false);
  // The cursors that led to each page after the first: Previous pops one.
  const [cursors, setCursors] = useState<string[]>([]);
  const firstPage = () => setCursors([]);

  // Typing is not a query. Without this every keystroke is a server-side read
  // of up to a full page's budget, and the answers race, so the table can
  // settle on the results for a prefix of what is in the box. Longer than the
  // sessions and units tables' 500ms, since each query here is also recorded.
  const timer = useRef<number>(0);
  // By value: text typed and then deleted is a new object but the same query.
  const pending = JSON.stringify(filters) !== JSON.stringify(applied);
  useEffect(() => {
    // Nothing typed since the last query (including on first load): leave the
    // page alone, or a quick click on Next gets sent back to page 1.
    if (!pending) return;
    clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      setApplied(filters);
      firstPage();
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [filters, pending]);

  // Typing a number of days is not a query either (every query is itself recorded
  // as a view of the log): it applies once the operator pauses.
  // A date typed by keyboard is a new valid date at each keystroke: it applies once
  // the operator pauses, like the other fields, and only while Dates is chosen.
  useEffect(() => {
    if (period !== "dates" || (datesText.from === dates.from && datesText.to === dates.to)) return;
    const t = window.setTimeout(() => { setDates(datesText); setAnchor(Date.now()); firstPage(); },
                                FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [period, datesText, dates]);

  // Only while Recent is chosen: leaving it cancels a number still being typed.
  useEffect(() => {
    const n = Number(daysText);
    if (period !== "recent" || n === days) return;
    const t = window.setTimeout(() => { setDays(n); setAnchor(Date.now()); firstPage(); },
                                FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [period, daysText, days]);

  const timeWindow = React.useMemo(
    () => windowOf(range, anchor),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period, days, dates.from, dates.to, anchor]);
  const from = "from" in timeWindow ? timeWindow.from : "";
  const to = "to" in timeWindow ? timeWindow.to : "";

  const query = {
    from: from || undefined,
    to: to || undefined,
    all: "all" in timeWindow || undefined,
    actor: applied.actor || undefined,
    endpoint: applied.endpoint || undefined,
    path: applied.path || undefined,
    status: applied.status || undefined,
    include_bodies: includeBodies || undefined,
    include_views: includeViews || undefined,
    limit: PAGE_SIZE,
    cursor: cursors[cursors.length - 1],
  };

  const q = useQuery({
    // The anchor too: with the whole history, Refresh re-reads up to a new "now".
    queryKey: ["audit", query, anchor],
    queryFn: () => fetchAudit(query),
    enabled: !("problem" in timeWindow),
    // Answers can hold customer personal data; keep none once off screen.
    gcTime: 0,
    // Every request reads up to the server's budget and is itself recorded as a
    // view, so a retry costs both; the API's errors say what to change.
    retry: false,
  });
  const cov = q.data?.coverage;

  const firstRecorded = cov?.days.find((d) => d.recorded)?.day;
  const entries = q.data?.entries ?? [];
  const next = q.data?.next_cursor ?? null;
  const searchedTo = q.data?.searched_to ?? null;

  const setFilter = (k: keyof FilterState) => (v: string) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const pickPeriod = (next: Period) => {
    setPeriod(next);
    setAnchor(Date.now());
    firstPage();
  };
  const setDate = (end: "from" | "to") => (value: string) =>
    setDatesText((d) => ({ ...d, [end]: value }));

  const refresh = () => {
    setAnchor(Date.now());
    firstPage();
  };

  return (
    <div className="space-y-5 pt-[86px]">
      <div>
        <h1 className="text-2xl font-semibold">{t("operator.nav.audit_log")}</h1>
        <div className="text-sm opacity-70">
          {t("operator.ops.audit.subtitle")}
        </div>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label>{t("operator.ops.audit.period")}</Label>
              <div className="mt-1 flex gap-1" role="group" aria-label={t("operator.ops.audit.period")}>
                {(["recent", "dates", "all"] as const).map((p) => (
                  <Button key={p} size="sm" aria-pressed={period === p}
                          variant={period === p ? "default" : "outline"}
                          onClick={() => pickPeriod(p)}>
                    {t("operator.ops.audit.period_option", { period: p })}
                  </Button>
                ))}
              </div>
            </div>
            {period === "recent" && (
              <div className="w-28">
                <Label htmlFor="audit-days">{t("operator.ops.audit.days")}</Label>
                <Input id="audit-days" type="number" min={1} max={MAX_WINDOW_DAYS} className="mt-1"
                       value={daysText} onChange={(e) => setDaysText(e.target.value)} />
              </div>
            )}
            {period === "dates" && (
              <div className="flex items-end gap-2">
                <div>
                  <Label htmlFor="audit-from">{t("operator.ops.audit.from")}</Label>
                  <Input id="audit-from" type="date" className="mt-1" max={dayString(Date.now())}
                         value={datesText.from} onChange={(e) => setDate("from")(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="audit-to">{t("operator.ops.audit.to")}</Label>
                  <Input id="audit-to" type="date" className="mt-1" max={dayString(Date.now())}
                         value={datesText.to} onChange={(e) => setDate("to")(e.target.value)} />
                </div>
              </div>
            )}
            <div className="min-w-[150px]">
              <Label>{t("operator.ops.audit.actor")}</Label>
              <Input className="mt-1" placeholder={t("operator.ops.audit.actor_placeholder")}
                     value={filters.actor} onChange={(e) => setFilter("actor")(e.target.value)} />
            </div>
            <div className="min-w-[150px]">
              <Label>{t("operator.ops.audit.endpoint")}</Label>
              <Input className="mt-1" placeholder="api_unit_settings"
                     value={filters.endpoint} onChange={(e) => setFilter("endpoint")(e.target.value)} />
            </div>
            <div className="min-w-[170px]">
              <Label>{t("operator.ops.audit.path_contains")}</Label>
              <Input className="mt-1" placeholder="3737311969"
                     value={filters.path} onChange={(e) => setFilter("path")(e.target.value)} />
            </div>
            <div className="w-28">
              <Label>{t("operator.ops.audit.status")}</Label>
              <Input className="mt-1" placeholder="4xx"
                     value={filters.status} onChange={(e) => setFilter("status")(e.target.value)} />
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch id="audit-bodies" checked={includeBodies}
                      onCheckedChange={(v) => setIncludeBodies(!!v)} />
              <Label htmlFor="audit-bodies" className="cursor-pointer">
                {t("operator.ops.audit.request_bodies")}
              </Label>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch id="audit-views" checked={includeViews}
                      onCheckedChange={(v) => { setIncludeViews(!!v); firstPage(); }} />
              <Label htmlFor="audit-views" className="cursor-pointer">
                {t("operator.ops.audit.views")}
              </Label>
            </div>
            <Button size="sm" variant="outline" onClick={refresh} className="mb-1">
              {t("common.action.refresh")}
            </Button>
          </div>

          {/* The window is pinned so paging stays consistent; say so, since a
              table that does not move on its own otherwise looks stale. */}
          <div className="mt-2 text-xs opacity-50">
            {"problem" in timeWindow ? (
              <span role="alert" className="text-destructive opacity-100">
                {t("operator.ops.audit.window_problem", { problem: timeWindow.problem, max: MAX_WINDOW_DAYS })}
              </span>
            ) : "all" in timeWindow ? (
              // The window starts a day before the oldest file (an entry stamped
              // before midnight can be in the next day's file): name the first day
              // with a record, not the window's start.
              <>{firstRecorded ? t("operator.ops.audit.everything_since", { day: firstRecorded }) : t("operator.ops.audit.everything")}</>
            ) : (
              <>{t("operator.ops.audit.window", { from: viewerInstant(from, "dateTimeSeconds") ?? "", to: viewerInstant(to, "dateTimeSeconds") ?? "" })}</>
            )}
            {pending && <span className="ml-2 opacity-80">{t("operator.ops.audit.filtering")}</span>}
          </div>

          {includeBodies && (
            <div role="note" className="mt-3 rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-xs text-warning-content">
              {t("operator.ops.audit.bodies_note")}
            </div>
          )}
        </CardContent>
      </Card>

      {cov && (
        <Card className={cov.directory_ok && cov.logging_enabled ? undefined : "border-destructive/50"}>
          <CardContent className="flex flex-wrap items-center gap-4 py-3">
            <CoverageBar t={t} days={cov.days} />
            <div className={`text-xs ${cov.directory_ok && cov.logging_enabled ? "opacity-70" : "text-destructive"}`}>
              {cov.directory_ok
                ? t("operator.ops.audit.coverage_known", { recorded: cov.days_recorded, total: cov.days.length })
                : t("operator.ops.audit.coverage_unknown")}{" "}
              {cov.note}
              {/* Said plainly because the strip sits directly above a filtered
                  table: with an actor filter narrowing the table to 3 rows a
                  day's bar still reads 12. Without this it looks like a
                  histogram of the rows below. */}
              {" "}{t("operator.ops.audit.coverage_scope")}
            </div>
          </CardContent>
        </Card>
      )}

      {q.isError ? (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {/* The API names the outcome (bad timestamp, range too wide, unparseable
              status filter) in `l10n`; a fixed string would leave the operator
              with no reason and nothing to correct. */}
          {errorMessage(t, q.error, { fallback: "operator.ops.audit.load_failed" })}
        </div>
      ) : (
        <DataTableShell>
          <DataTable
            columns={columns}
            data={entries}
            isLoading={q.isLoading}
            tableClassName="text-xs"
            emptyMessage={t("operator.ops.audit.empty")}
          />
        </DataTableShell>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs opacity-60">
          {/* No total: pages are read on demand, so the count is never known
              up front. A page cut short says how far back it searched. */}
          {t("operator.ops.audit.page", { page: cursors.length + 1, count: entries.length })}
          {searchedTo && (
            <span className="ml-2">
              {t("operator.ops.audit.searched_back", { time: viewerInstant(searchedTo, "dateTimeSeconds") ?? "" })}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={cursors.length === 0}
                  onClick={() => setCursors((c) => c.slice(0, -1))}>
            {t("operator.ops.audit.previous")}
          </Button>
          <Button size="sm" variant="outline" disabled={!next}
                  onClick={() => next && setCursors((c) => [...c, next])}>
            {searchedTo ? t("operator.ops.audit.keep_searching") : t("operator.ops.page_next")}
          </Button>
        </div>
      </div>

      {/* Shown, not hidden in a doc: these are the limits of what the numbers
          above can mean, and they come from the API with every response. */}
      {q.data?.caveats?.length ? (
        <ul className="space-y-1 border-t pt-3 text-xs opacity-60">
          {q.data.caveats.map((c) => (
            <li key={c}>• {c}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
